import { useEffect, useState } from 'react'
import { Hero } from '@/components/Hero'
import { AnimeGrid } from '@/components/AnimeGrid'
import { CategoryTabs } from '@/components/CategoryTabs'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { anilistService } from '@/services/anilist'
import { Anime } from '@/types/anime'

export default function Home() {
  const [trendingAnime, setTrendingAnime] = useState<Anime[]>([])
  const [popularAnime, setPopularAnime] = useState<Anime[]>([])
  const [topRatedAnime, setTopRatedAnime] = useState<Anime[]>([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('trending')

  useEffect(() => {
    const fetchAnimeData = async () => {
      try {
        setLoading(true)
        
        // Fetch each category separately with error handling
        const trendingPromise = anilistService.getTrendingAnime(12).catch(err => {
          console.error('Error fetching trending anime:', err)
          return []
        })
        
        const popularPromise = anilistService.getPopularAnime(12).catch(err => {
          console.error('Error fetching popular anime:', err)
          return []
        })
        
        const topRatedPromise = anilistService.getTopRatedAnime(12).catch(err => {
          console.error('Error fetching top-rated anime:', err)
          return []
        })
        
        const [trending, popular, topRated] = await Promise.all([
          trendingPromise,
          popularPromise,
          topRatedPromise
        ])
        
        setTrendingAnime(trending || [])
        setPopularAnime(popular || [])
        setTopRatedAnime(topRated || [])
      } catch (error) {
        console.error('Error fetching anime data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAnimeData()
  }, [])

  const getCurrentAnimeList = () => {
    switch (activeCategory) {
      case 'trending':
        return trendingAnime
      case 'popular':
        return popularAnime
      case 'top-rated':
        return topRatedAnime
      default:
        return trendingAnime
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <Hero featuredAnime={trendingAnime[0]} />
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Category Navigation */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Discover Anime
          </h2>
          <CategoryTabs
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>

        {/* Anime Grid */}
        <AnimeGrid animeList={getCurrentAnimeList()} />

        {/* Additional Sections */}
        {activeCategory === 'trending' && (
          <>
            <div className="mt-16">
              <h3 className="text-2xl font-bold mb-6 text-white">
                Popular This Season
              </h3>
              <AnimeGrid animeList={popularAnime.slice(0, 8)} />
            </div>

            <div className="mt-16">
              <h3 className="text-2xl font-bold mb-6 text-white">
                Top Rated Anime
              </h3>
              <AnimeGrid animeList={topRatedAnime.slice(0, 8)} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
